import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Routes, Route, Link, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

import DashboardSidebar from './components/Dashboard/DashboardSidebar.jsx';
import DashboardNavbar from './components/Dashboard/DashboardNavbar.jsx'; // Import Navbar
import { ToolsDataProvider, useToolsData } from './context/ToolsDataContext.jsx';
import { supabase } from './supabaseClient.js'; // Import supabase
import CacheService from './services/CacheService.js'; // Import CacheService
import ScrollToTop from './components/ScrollToTop.jsx';

import HomePage from './pages/HomePage.jsx';
import AboutPage from './pages/AboutPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import TopupPage from './pages/TopupPage.jsx';
import AffiliatorPage from './pages/AffiliatorPage.jsx';
import RiwayatGeneratePage from './pages/RiwayatGeneratePage.jsx';
import TopupReturn from './pages/TopupReturn.jsx';
import DashboardAdminPage from './pages/DashboardAdminPage.jsx';
import AdminSidebarItemsPage from './pages/AdminSidebarItemsPage.jsx';
import AdminAffiliatePage from './pages/AdminAffiliatePage.jsx';
import AdminPackagesPage from './pages/AdminPackagesPage.jsx';
import AdminTransactionsPage from './pages/AdminTransactionsPage.jsx';
import AdminRevenuePage from './pages/AdminRevenuePage.jsx';
import AdminUsersPage from './pages/AdminUsersPage.jsx';
import AdminBlogPage from './pages/AdminBlogPage.jsx';
import AdminBlogEditorPage from './pages/AdminBlogEditorPage.jsx';
import GenericAiPage from './pages/GenericAiPage.jsx';
import BlogListPage from './pages/BlogListPage.jsx';
import BlogPostPage from './pages/BlogPostPage.jsx';
import AdminProvidersPage from './pages/AdminProvidersPage.jsx';
import AdminActivityPage from './pages/AdminActivityPage.jsx';
import AdminModelsRouter from './pages/AdminModelsRouter.jsx';
import AdminFeaturesPage from './pages/AdminFeaturesPage.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import logoNeoAI from './assets/Asset 4.svg';
import iconNeoAI from './assets/icon.svg';

// 🔹 Navbar Public Component (Floating Island Design)
const NavbarPublic = ({ t, i18n, logoNeoAI, iconNeoAI, headingFont, isOpen, setIsOpen, isLangMenuOpen, setIsLangMenuOpen, closeMenu, NavLink, MobileNavLink }) => {
  const [scrolled, setScrolled] = useState(false);
  const neoGradient = 'radial-gradient(circle, #00a4ff 0%, #00a4ff 85%, transparent 100%)';

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Logic:
          // 1. At top (< 10px) -> Island View (false)
          // 2. Scroll Down (> 50px) -> Expanded View (true)
          // 3. Scroll Up (ANY amount) -> Island View (false) - INSTANT REVEAL

          if (currentScrollY < 10) {
            setScrolled(false);
          } else if (currentScrollY > lastScrollY && currentScrollY > 50) {
            // Only hide/expand if scrolling DOWN and not at the very top
            setScrolled(true);
          } else if (currentScrollY < lastScrollY) {
            // Immediately show island view if scrolling UP
            setScrolled(false);
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 w-full px-4 pointer-events-none transition-all duration-500 ease-in-out ${scrolled ? 'max-w-7xl' : 'md:max-w-fit'}`}>
      <nav className={`pointer-events-auto flex items-center transition-all duration-500 justify-end ${scrolled ? 'md:justify-between' : 'md:gap-4 md:justify-center'}`}>

        {/* GROUP 1: LOGO (Left Island) */}
        <div className={`hidden md:flex h-14 items-center bg-white/80 backdrop-blur-md border border-white/50 shadow-sm rounded-full px-5 transition-all duration-500 hover:bg-white/95 ${scrolled ? 'shadow-md' : ''} md:mr-0`}>
          <Link to="/" onClick={closeMenu} className="flex items-center gap-2">
            <img
              src={iconNeoAI}
              alt="NeoAI"
              className="h-9 w-9 object-contain min-w-[36px]"
              width={36}
              height={36}
            />
          </Link>
        </div>

        {/* GROUP 2: NAV LINKS + LANG (Center Island) */}
        {!scrolled && (
          <div className="hidden md:flex items-center gap-6 bg-white/80 backdrop-blur-md border border-white/50 shadow-sm rounded-full px-6 h-14 hover:bg-white/95">
            <NavLink to="/" label={t('nav.home')} />
            <NavLink to="/about" label={t('nav.about')} />
            <NavLink to="/blog" label={t('nav.blog')} />

            <div className="w-px h-4 bg-slate-300 mx-2"></div>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                onBlur={() => setTimeout(() => setIsLangMenuOpen(false), 200)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-blue-50 text-slate-600 font-bold text-xs transition-all"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>{i18n.resolvedLanguage === 'en' ? 'EN' : 'ID'}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {/* Dropdown */}
              <div
                className={`absolute top-full right-0 mt-3 w-32 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden transform transition-all origin-top-right
                  ${isLangMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'}`}
              >
                <button onClick={() => { i18n.changeLanguage('en'); setIsLangMenuOpen(false); }} className={`w-full text-left px-4 py-2 text-xs font-bold hover:bg-slate-50 flex justify-between ${i18n.resolvedLanguage === 'en' ? 'text-[#00a4ff]' : 'text-slate-600'}`}>
                  <span>English</span>
                </button>
                <button onClick={() => { i18n.changeLanguage('id'); setIsLangMenuOpen(false); }} className={`w-full text-left px-4 py-2 text-xs font-bold hover:bg-slate-50 flex justify-between ${i18n.resolvedLanguage === 'id' ? 'text-[#00a4ff]' : 'text-slate-600'}`}>
                  <span>Indonesia</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GROUP 3: LOGIN (Right Island) */}
        <div className="flex items-center gap-2 relative z-50">
          <Link
            to="/login"
            className="hidden md:flex h-14 items-center relative group rounded-full transition-all duration-300 hover:shadow-md"
          >
            {/* Background Layer (Gradient Border Effect) - Always Active */}
            <div
              className="absolute inset-0 rounded-full transition-all duration-300"
              style={{ background: neoGradient }}
            />

            {/* White Mask Layer - Always Visible */}
            <div
              className="absolute inset-[2px] rounded-full bg-white transition-all duration-300"
            />

            {/* Text Layer - Always Slate 800 */}
            <span className="relative z-10 flex items-center h-full px-8 font-bold text-sm text-slate-800 transition-colors duration-300 whitespace-nowrap">
              {t('nav.login')}
            </span>
          </Link>

          {/* Mobile Menu Button (Only Visible on Mobile) */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden bg-white backdrop-blur-md border border-white/50 p-2 rounded-full shadow-sm text-slate-700 hover:text-[#00a4ff]"
            aria-label="Toggle mobile menu"
          >
            {isOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            )}
          </button>
        </div>

        {/* MOBILE MENU DROPDOWN (Fullscreen Overlay) */}
        {/* MOBILE MENU DROPDOWN (Fullscreen Overlay - Portaled to Body) */}
        {typeof document !== 'undefined' && createPortal(
          <div
            className={`
               md:hidden fixed inset-0 !bg-white flex flex-col pt-28 pb-10 px-8 transition-all duration-300 z-40
               ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
             `}
            style={{ backgroundColor: '#ffffff' }}
          >

            {/* Menu Content */}
            <div className="flex flex-col w-full h-full">
              {/* Logo */}
              <img src={logoNeoAI} alt="NeoAI" className="h-8 w-auto mb-8 self-start" width={120} height={32} />

              {/* Links List */}
              <div className="flex flex-col w-full space-y-0">
                <MobileNavLink to="/" label={t('nav.home')} onClick={closeMenu} />
                <MobileNavLink to="/about" label={t('nav.about')} onClick={closeMenu} />
                <MobileNavLink to="/blog" label={t('nav.blog')} onClick={closeMenu} />
              </div>

              {/* Bottom Actions: Language & Login */}
              <div className="mt-auto w-full flex flex-col gap-4">
                {/* Language Switcher Row */}
                <div className="flex items-center justify-between border-t border-slate-100 pt-6">
                  <span className="text-sm font-bold text-slate-500 uppercase tracking-widest">{t('nav.language')}</span>
                  <div className="flex gap-2">
                    <button onClick={() => i18n.changeLanguage('en')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${i18n.resolvedLanguage === 'en' ? 'bg-[#164361] text-white' : 'bg-slate-100 text-slate-600'}`}>EN</button>
                    <button onClick={() => i18n.changeLanguage('id')} className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${i18n.resolvedLanguage === 'id' ? 'bg-[#164361] text-white' : 'bg-slate-100 text-slate-600'}`}>ID</button>
                  </div>
                </div>

                {/* Login Button */}
                <Link
                  to="/login"
                  onClick={closeMenu}
                  className="w-full py-4 bg-[#164361] text-white text-center font-bold text-sm uppercase tracking-widest rounded-none hover:bg-[#2299ED] transition-colors"
                >
                  {t('nav.login')}
                </Link>
              </div>
            </div>
          </div>,
          document.body
        )}

      </nav>
    </header>
  );
};

// --- INNER DASHBOARD LAYOUT (with prefetch trigger) ---
const DashboardLayoutInner = () => {
  const { prefetchAllTools } = useToolsData();
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [navbarOptions, setNavbarOptions] = useState(null);

  // Global State for Dashboard
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(() => CacheService.get('user_profile') || null);

  // Prefetch all tools & Fetch User Data
  useEffect(() => {
    prefetchAllTools();

    const fetchUser = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        setUser(authUser);
        // Fetch Profile if not in cache or to refresh
        const { data: profileRes } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (profileRes) {
          setProfile(profileRes);
          CacheService.set('user_profile', profileRes, 60);
        }
      }
    };
    fetchUser();
  }, [prefetchAllTools]);

  // Listen for profile updates from other components
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      if (event.detail) {
        setProfile(event.detail);
      }
    };
    window.addEventListener('profile-updated', handleProfileUpdate);
    return () => window.removeEventListener('profile-updated', handleProfileUpdate);
  }, []);

  // Realtime Profile Subscription (Credits, etc.)
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`public:profiles:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          //   console.log('Profile updated via Realtime:', payload.new);
          setProfile(payload.new);
          CacheService.set('user_profile', payload.new, 60);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const displayName = profile?.username || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'User';

  return (
    <div className="flex h-screen w-full overflow-hidden bg-[#F8FAFC] font-sans">
      <DashboardSidebar />
      <div className="flex-1 flex flex-col min-w-0 min-h-0 relative h-full">
        <DashboardNavbar
          displayName={displayName}
          creditBalance={profile?.credit_balance}
          walletBalance={profile?.wallet_balance || 0}
          roleLabel={profile?.role === 'admin' ? 'ADMIN' : 'Member'}
          pageTitle={pageTitle}
          navbarOptions={navbarOptions}
        />
        <div className="flex-1 relative overflow-auto"> {/* Container for pages */}
          <Outlet context={{ setPageTitle, user, profile, setNavbarOptions }} />
        </div>
      </div>
    </div>
  );
};

// --- DASHBOARD LAYOUT with Provider wrapper ---
const DashboardLayout = () => {
  return (
    <ToolsDataProvider>
      <DashboardLayoutInner />
    </ToolsDataProvider>
  );
};

function App() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);



  const isDashboardPage =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/dashboardadmin');

  // Pages where the navbar floats over the content (no top padding needed on main)
  const isTransparentNavPage =
    location.pathname === '/' ||
    location.pathname === '/about' ||
    location.pathname.startsWith('/blog');

  const closeMenu = () => setIsOpen(false);

  // 🌍 Toggle Language Helper
  const toggleLanguage = () => {
    const currentLang = i18n.resolvedLanguage;
    const nextLang = currentLang === 'en' ? 'id' : 'en';
    i18n.changeLanguage(nextLang);
  };


  // 🔤 Fonts (sama seperti di HomePage)
  const headingFont = {
    fontFamily:
      '"Special Gothic Expanded One", "Kohinoor Devanagari", system-ui, sans-serif',
  };

  const bodyFont = {
    fontFamily:
      '"Kohinoor Devanagari", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };

  return (
    <div
      className="min-h-screen w-full bg-[#F6F9FC] flex flex-col overflow-x-hidden text-[#164361]"
      style={bodyFont}
    >
      <ScrollToTop />
      {/* NAVBAR PUBLIC (disembunyikan saat di /dashboard/...) */}
      {!isDashboardPage && (
        <NavbarPublic t={t} i18n={i18n} logoNeoAI={logoNeoAI} iconNeoAI={iconNeoAI} headingFont={headingFont} isOpen={isOpen} setIsOpen={setIsOpen} isLangMenuOpen={isLangMenuOpen} setIsLangMenuOpen={setIsLangMenuOpen} closeMenu={closeMenu} NavLink={NavLink} MobileNavLink={MobileNavLink} />
      )}

      <main className={`grow w-full ${!isDashboardPage && !isTransparentNavPage ? 'pt-20' : ''}`}>
        <Routes>
          {/* PUBLIC PAGES */}
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/login" element={<LoginPage />} />

          {/* DASHBOARD PAGES */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard/topup" element={<TopupPage />} />
              <Route path="/dashboard/topup/return" element={<TopupReturn />} />
              <Route path="/dashboard/affiliator" element={<AffiliatorPage />} />

              <Route path="/dashboard/riwayat-generate" element={<RiwayatGeneratePage />} />

              <Route path="/dashboard/feature/:slug/:sessionId?" element={<GenericAiPage />} />
            </Route>
          </Route>

          {/* 🔹 DASHBOARD ADMIN */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route path="/dashboardadmin" element={<DashboardAdminPage />} />
            <Route
              path="/dashboardadmin/sidebar-items"
              element={<AdminSidebarItemsPage />}
            />
            <Route
              path="/dashboardadmin/affiliate"
              element={<AdminAffiliatePage />}
            />
            <Route
              path="/dashboardadmin/packages"
              element={<AdminPackagesPage />}
            />
            <Route
              path="/dashboardadmin/transactions"
              element={<AdminTransactionsPage />}
            />
            <Route
              path="/dashboardadmin/revenue"
              element={<AdminRevenuePage />}
            />
            <Route path="/dashboardadmin/users" element={<AdminUsersPage />} />

            {/* Admin Blog */}
            <Route path="/dashboardadmin/blog" element={<AdminBlogPage />} />
            <Route path="/dashboardadmin/blog/new" element={<AdminBlogEditorPage />} />
            <Route path="/dashboardadmin/blog/edit/:id" element={<AdminBlogEditorPage />} />

            {/* Admin Providers */}
            {/* Admin Providers */}
            <Route path="/dashboardadmin/providers" element={<AdminProvidersPage />} />
            <Route path="/dashboardadmin/activity" element={<AdminActivityPage />} />

            {/* 🔹 ROUTE MODEL AI (pakai router khusus) */}
            <Route
              path="/dashboardadmin/models"
              element={<AdminModelsRouter />}
            />
            <Route
              path="/dashboardadmin/models/:featureCode"
              element={<AdminModelsRouter />}
            />
            <Route path="/dashboardadmin/features" element={<AdminFeaturesPage />} />
          </Route>

          {/* 404 */}
          <Route
            path="*"
            element={
              <h1
                className="text-center mt-10 text-2xl text-[#164361]"
                style={headingFont}
              >
                404 - Not Found
              </h1>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

const NavLink = ({ to, label }) => (
  <Link
    to={to}
    className="relative group text-[#475569] hover:text-[#2299ED] font-medium transition-colors duration-200 whitespace-nowrap"
  >
    {label}
    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#2299ED] transition-all duration-300 group-hover:w-full" />
  </Link>
);

const MobileNavLink = ({ to, label, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="block w-full py-5 text-xl font-bold text-[#164361] uppercase tracking-wider border-b border-slate-100 hover:text-[#2299ED] hover:pl-2 transition-all"
  >
    {label}
  </Link>
);

export default App;
